import React, { useCallback, useEffect, useMemo } from "react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { compose } from "redux";
import yup from "yup";
import moment from "moment";
import { EcheqsMassivesManagementList, Button } from "@ui-kit/components/index.es";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import WrongEnvironmentModal from "pages/communications/_components/WrongEnvironmentModal";
import { resizableRoute } from "pages/_components/Resizable";
import { actions as communicationActions, selectors as communicationSelectors } from "reducers/communication";
import {
    actions as echeqMassManagementActions,
    selectors as echeqMassManagementSelectors,
} from "reducers/echeqMassManagement";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import {
    applyEmbeddedV3ShellLayoutState,
    clearEmbeddedV3ShellLayoutState,
    isUxDashboardImprovementsEnabled,
} from "util/v3ShellLayoutSync";
import { getPostLoginHomePath } from "util/v3UxConfig";

import { ECHEQS_MASS_MANAGEMENT_LIST_PATH, ECHEQS_MASS_MANAGEMENT_NEW_PATH } from "./echeqMassManageConstants";
import { buildMassEcheqManageBatchDetailLocation } from "./echeqMassManageBatchDetailUtils";
import {
    buildActionFilterOptions,
    buildStatusFilterOptions,
    DEFAULT_LIST_ORDER_BY,
    DEFAULT_LIST_SORT_DIRECTION,
    getDefaultListFilters,
    mapBatchToCardProps,
    mapFiltersFormValuesToListFilters,
    mapListFiltersToFormValues,
    validateFilterAmountRange,
} from "./echeqMassManageListUtils";
import { createI18nLabelResolver, resolveMassEcheqManagementCapabilities } from "./echeqMassManagePageUtils";
import { canAccessMassEcheqManagementForm, isMassEcheqManagementEnabled } from "./massEcheqManagementFlags";

export {
    ECHEQS_MASS_MANAGEMENT_CONFIRM_PATH,
    ECHEQS_MASS_MANAGEMENT_LIST_PATH,
    ECHEQS_MASS_MANAGEMENT_NEW_PATH,
    ECHEQS_MASS_MANAGEMENT_TICKET_PATH,
} from "./echeqMassManageConstants";

const FORM_ID = "echeqs.massives.manage.list";
const FILTERS_ID = `${FORM_ID}.filters`;

function EcheqsMassManageList(props) {
    const {
        dispatch,
        isDesktop,
        activeEnvironment,
        embeddedInV3Shell,
        batchList,
        listFilters,
        listFiltersCustomized,
        pageNumber,
        totalPages,
        fetchingList,
        fetchingBatchDownload,
        location,
        isWrongEnvironmentModalVisible,
        correctEnvironmentName,
        wrongEnvironmentMessageKey,
    } = props;

    const permissions = activeEnvironment?.permissions || {};
    const { canAdmit, canPay, canCreateBatch: showMassiveManagementButton } = resolveMassEcheqManagementCapabilities(
        permissions,
    );

    const resolveLabel = useMemo(() => createI18nLabelResolver(), []);

    const getFilterLabel = useCallback((key) => i18n.get(`${FILTERS_ID}.${key}`), []);

    const getActionLabel = useCallback((action) => i18n.get(`echeq.massive.manage.action.${action}`), []);

    const allowedMonths = useMemo(
        () => configUtils.getInteger("frontend.managementMassiveEcheq.allowedPeriod.months", 6),
        [],
    );

    const statusFilterOptions = useMemo(() => buildStatusFilterOptions(getFilterLabel), [getFilterLabel]);

    const actionFilterOptions = useMemo(
        () => buildActionFilterOptions(canAdmit, canPay, getFilterLabel, getActionLabel),
        [canAdmit, canPay, getActionLabel, getFilterLabel],
    );

    const filterInitialValues = useMemo(
        () =>
            mapListFiltersToFormValues(getDefaultListFilters(allowedMonths), {
                statusOptions: statusFilterOptions,
                actionOptions: actionFilterOptions,
            }),
        [actionFilterOptions, allowedMonths, statusFilterOptions],
    );

    const filterDefaultValues = useMemo(
        () =>
            mapListFiltersToFormValues(listFilters, {
                statusOptions: statusFilterOptions,
                actionOptions: actionFilterOptions,
            }),
        [actionFilterOptions, listFilters, statusFilterOptions],
    );

    const filtersValidationSchema = useMemo(
        () =>
            yup.object().shape({
                processDates: yup
                    .array()
                    .nullable()
                    .test("process-dates-required", i18n.get(`${FILTERS_ID}.processDates.required`), (value) => {
                        if (!Array.isArray(value) || value.length < 2) {
                            return false;
                        }
                        const [from, to] = value;
                        return (
                            from instanceof Date &&
                            !Number.isNaN(from.getTime()) &&
                            to instanceof Date &&
                            !Number.isNaN(to.getTime())
                        );
                    }),
                amountTo: yup
                    .mixed()
                    .test("amount-range", i18n.get(`${FILTERS_ID}.amountRange.invalid`), validateFilterAmountRange),
            }),
        [],
    );

    const loadBatchList = useCallback(
        (page = 1, { force = false } = {}) => {
            // Evita requests duplicados mientras hay un fetch en curso (scroll infinito mobile).
            if (!force && fetchingList) {
                return;
            }
            dispatch(
                echeqMassManagementActions.listRequest(
                    {
                        ...listFilters,
                        pageNumber: page,
                    },
                    isDesktop,
                ),
            );
        },
        [dispatch, fetchingList, isDesktop, listFilters],
    );

    const handleFilterSubmit = useCallback(
        (formValues) => {
            const filters = {
                ...mapFiltersFormValuesToListFilters(formValues, 1),
                orderBy: listFilters.orderBy || DEFAULT_LIST_ORDER_BY,
                direction: listFilters.direction || DEFAULT_LIST_SORT_DIRECTION,
            };
            dispatch(echeqMassManagementActions.listRequest(filters, isDesktop, true));
        },
        [dispatch, isDesktop, listFilters.direction, listFilters.orderBy],
    );

    const handleClearFilters = useCallback(() => {
        const filters = {
            ...mapFiltersFormValuesToListFilters(filterInitialValues, 1),
            orderBy: DEFAULT_LIST_ORDER_BY,
            direction: DEFAULT_LIST_SORT_DIRECTION,
        };
        dispatch(echeqMassManagementActions.listRequest(filters, isDesktop, false));
    }, [dispatch, filterInitialValues, isDesktop]);

    const handleOrderByChange = useCallback(
        ({ orderBy, direction }) => {
            dispatch(
                echeqMassManagementActions.listRequest(
                    {
                        ...listFilters,
                        orderBy: orderBy || DEFAULT_LIST_ORDER_BY,
                        direction: direction || DEFAULT_LIST_SORT_DIRECTION,
                        pageNumber: 1,
                    },
                    isDesktop,
                ),
            );
        },
        [dispatch, isDesktop, listFilters],
    );

    const orderByProps = useMemo(
        () => ({
            propList: [{ value: DEFAULT_LIST_ORDER_BY, label: getFilterLabel("processDates") }],
            defaultValueSelector: listFilters.orderBy || DEFAULT_LIST_ORDER_BY,
            defaultValueRadioButtons: listFilters.direction || DEFAULT_LIST_SORT_DIRECTION,
            onChange: handleOrderByChange,
        }),
        [getFilterLabel, handleOrderByChange, listFilters.direction, listFilters.orderBy],
    );

    const filtersProps = useMemo(
        () => ({
            initialValues: filterInitialValues,
            defaultValues: listFiltersCustomized ? filterDefaultValues : undefined,
            getLabels: getFilterLabel,
            statusOptions: statusFilterOptions,
            actionOptions: actionFilterOptions,
            onSubmit: handleFilterSubmit,
            onClearFilters: handleClearFilters,
            validationSchema: filtersValidationSchema,
            loading: fetchingList,
            enableUSD: true,
            amountMaxLength: 15,
            processDatesMinDate: moment()
                .subtract(allowedMonths, "months")
                .startOf("day")
                .toDate(),
            processDatesMaxDate: moment()
                .endOf("day")
                .toDate(),
            orderByProps,
        }),
        [
            actionFilterOptions,
            allowedMonths,
            filterDefaultValues,
            filterInitialValues,
            filtersValidationSchema,
            fetchingList,
            getFilterLabel,
            handleClearFilters,
            handleFilterSubmit,
            listFiltersCustomized,
            orderByProps,
            statusFilterOptions,
        ],
    );

    const desktopHomePath = getPostLoginHomePath(isUxDashboardImprovementsEnabled());

    const handleGoBack = useCallback(() => {
        dispatch(push(desktopHomePath));
    }, [dispatch, desktopHomePath]);

    const handleMassiveManagement = useCallback(() => {
        dispatch(echeqMassManagementActions.cleanFormFlow());
        dispatch(push(ECHEQS_MASS_MANAGEMENT_NEW_PATH));
    }, [dispatch]);

    const handleOpenBatchDetail = useCallback(
        (batchId) => {
            if (!batchId) {
                return;
            }
            const returnPathname =
                location?.pathname && location.pathname.startsWith("/")
                    ? location.pathname
                    : ECHEQS_MASS_MANAGEMENT_LIST_PATH;
            // Evita arrastrar el filtro de estado del detalle anterior al abrir otro lote.
            dispatch(echeqMassManagementActions.resetBatchDetailFilters());
            dispatch(push(buildMassEcheqManageBatchDetailLocation(batchId, returnPathname)));
        },
        [dispatch, location?.pathname],
    );

    const handleDownloadExcel = useCallback(
        (batchId) => {
            if (!batchId || fetchingBatchDownload) {
                return;
            }
            dispatch(echeqMassManagementActions.batchDownloadRequest(batchId));
        },
        [dispatch, fetchingBatchDownload],
    );

    const mobileRightContent = useMemo(() => {
        if (isDesktop || !showMassiveManagementButton) {
            return undefined;
        }

        return (
            <Button
                icon="Add"
                iconVariant="outline"
                color="white"
                variant="text"
                onClick={handleMassiveManagement}
                aria-label={i18n.get(`${FORM_ID}.massiveButton`)}
                size="large"
            />
        );
    }, [handleMassiveManagement, isDesktop, showMassiveManagementButton]);

    const syncEmbeddedV3ShellLayout = useCallback(() => {
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["echeqs"],
            titleKey: `${FORM_ID}.title`,
            goBackPath: desktopHomePath,
            rightContentMobile: mobileRightContent,
        });
    }, [dispatch, desktopHomePath, embeddedInV3Shell, isDesktop]);

    useEffect(() => {
        syncEmbeddedV3ShellLayout();
        return () => {
            if (embeddedInV3Shell) {
                const nextPathname =
                    typeof window !== "undefined" && window.location?.pathname ? window.location.pathname : "";
                clearEmbeddedV3ShellLayoutState(dispatch, { nextPathname });
            }
        };
    }, [dispatch, embeddedInV3Shell, syncEmbeddedV3ShellLayout]);

    useEffect(() => {
        syncEmbeddedV3ShellLayout();
    }, [syncEmbeddedV3ShellLayout]);

    useEffect(() => {
        if (location?.pathname !== ECHEQS_MASS_MANAGEMENT_LIST_PATH) {
            return;
        }
        dispatch(echeqMassManagementActions.setTicketReturnToBatchList(false));
        dispatch(
            echeqMassManagementActions.listRequest(
                {
                    ...listFilters,
                    pageNumber: listFilters.pageNumber || 1,
                },
                isDesktop,
            ),
        );
        // Solo al montar o volver al listado (cambio de ruta). No incluir listFilters/loadBatchList en deps.
    }, [dispatch, isDesktop, location?.pathname]);

    const handleCloseWrongEnvironmentModal = useCallback(() => {
        dispatch(communicationActions.hideWrongEnvironmentModal());
    }, [dispatch]);

    const cardItems = useMemo(
        () =>
            batchList.map((batch) => {
                const cardProps = mapBatchToCardProps(batch, resolveLabel);
                const canDownloadExcel = cardProps.status && cardProps.status !== "in_progress";
                return {
                    ...cardProps,
                    onClick: () => handleOpenBatchDetail(batch.idBatch),
                    onDownloadExcel: canDownloadExcel ? handleDownloadExcel : undefined,
                };
            }),
        [batchList, handleDownloadExcel, handleOpenBatchDetail, resolveLabel],
    );

    const listProps = useMemo(
        () => ({
            array: cardItems,
            loading: fetchingList || fetchingBatchDownload,
            emptyStateText: i18n.get(`${FORM_ID}.empty`),
            v3Layout: true,
            endMessage: i18n.get(`${FORM_ID}.endMessage`),
            paginationProps: {
                pageNumber,
                totalPages,
                action: loadBatchList,
            },
        }),
        [cardItems, fetchingList, fetchingBatchDownload, loadBatchList, pageNumber, totalPages],
    );

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="echeqs" />}
            <WrongEnvironmentModal
                isVisible={isWrongEnvironmentModalVisible}
                isDesktop={isDesktop}
                closeModal={handleCloseWrongEnvironmentModal}
                environment={correctEnvironmentName}
                warningMessageKey={wrongEnvironmentMessageKey}
            />
            {!embeddedInV3Shell && !isDesktop && (
                <div className="admin-detail-head px-0 mb-0">
                    <Head
                        onBack={handleGoBack}
                        title={`${FORM_ID}.title`}
                        rightContent={() => mobileRightContent}
                        headerClassName="blue-main-header-mobile blue-main-title-mobile background-transparent"
                    />
                </div>
            )}
            <EcheqsMassivesManagementList
                goBack={handleGoBack}
                title={i18n.get(`${FORM_ID}.title`)}
                massiveManagementLabel={i18n.get(`${FORM_ID}.massiveButton`)}
                showMassiveManagementButton={showMassiveManagementButton}
                onMassiveManagement={showMassiveManagementButton ? handleMassiveManagement : undefined}
                filtersProps={filtersProps}
                listProps={listProps}
            />
        </>
    );
}

EcheqsMassManageList.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    activeEnvironment: shape({
        permissions: shape({}),
    }).isRequired,
    embeddedInV3Shell: bool,
    location: shape({
        pathname: string,
    }),
    batchList: arrayOf(shape({})).isRequired,
    listFilters: shape({}).isRequired,
    listFiltersCustomized: bool.isRequired,
    pageNumber: number.isRequired,
    totalPages: number.isRequired,
    fetchingList: bool.isRequired,
    fetchingBatchDownload: bool,
    isWrongEnvironmentModalVisible: bool,
    correctEnvironmentName: string,
    wrongEnvironmentMessageKey: string,
};

EcheqsMassManageList.defaultProps = {
    embeddedInV3Shell: false,
    location: null,
    fetchingBatchDownload: false,
    isWrongEnvironmentModalVisible: false,
    correctEnvironmentName: null,
    wrongEnvironmentMessageKey: null,
};

const mapStateToProps = (state) => ({
    activeEnvironment: state.session.activeEnvironment,
    location: state.router?.location,
    batchList: echeqMassManagementSelectors.getBatchList(state),
    listFilters: echeqMassManagementSelectors.getListFilters(state),
    listFiltersCustomized: echeqMassManagementSelectors.getListFiltersCustomized(state),
    pageNumber: echeqMassManagementSelectors.getPageNumber(state),
    totalPages: echeqMassManagementSelectors.getTotalPages(state),
    fetchingList: echeqMassManagementSelectors.getFetchingList(state),
    fetchingBatchDownload: echeqMassManagementSelectors.getFetchingBatchDownload(state),
    isWrongEnvironmentModalVisible: communicationSelectors.isWrongEnvironmentModalVisible(state),
    correctEnvironmentName: communicationSelectors.correctEnvironmentName(state),
    wrongEnvironmentMessageKey: communicationSelectors.wrongEnvironmentMessageKey(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(EcheqsMassManageList);

export function canAccessMassEcheqManagementList(permissions = {}) {
    return isMassEcheqManagementEnabled() && !!permissions?.massiveEcheqManage;
}

export function canCreateMassEcheqManagementBatch(permissions = {}) {
    return canAccessMassEcheqManagementList(permissions) && canAccessMassEcheqManagementForm(permissions);
}

export { isMassEcheqManagementEnabled, canAccessMassEcheqManagementForm };
